import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS, ANIM, CASINO_SETTINGS, SPACE } from '../../../../theme/tokens';
import { CUST } from '../../../../theme/customizer-tokens';
import { cardEnterAt, fadeInWhenReady, hoverWash } from '../../../../theme/mixins';

/* Per-card edit affordance, top-right over the artwork. */
export const EditBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 999px;
  cursor: pointer;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.textBright};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
  }
`;

/* Covers the artwork while this creative is being regenerated: the SAME dot-field "atlas" texture
   the onboarding logo generation uses (a dark scrim behind a drifting monochrome dot lattice), with
   the status label layered above it. */
export const BusyOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: ${CUST.logoProcessingScrim};
`;

/* Lumio outline spark + status label, above the dot-field canvas. */
export const BusyInner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  gap: 8px;
`;

/* The Lumio outline spark (hollow gradient stroke) — the AI mark, matching the credits chip. */
export const BusySpark = styled.img`
  width: 20px;
  height: 20px;
`;

export const BusyText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textBright};
`;

/* Determinate progress bar for a regeneration, pinned as a bottom band over the dot-field (the scrim
   is always dark, so the default light-on-dark fill reads). A positioned child, not a grid child, so
   the overlay's place-items:center does not recentre it. */
export const BusyProgress = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 1;
`;

/* A banner with regenerations shows ONE lighter card peeking out beneath the front one — the only
   stack cue. Card + leaf share a single grid cell, so the leaf is always sized to the card, never
   to a taller grid row when cards of different heights sit on the same row. `isolation` keeps the
   negative-z leaf behind the card yet above the page; `align-content: start` stops the shared cell
   from stretching past the card's own height. */
export const CardStack = styled.div<{ $enterIndex?: number }>`
  /* A stacked card enters as one, leaves included. */
  ${({ $enterIndex }) => ($enterIndex === undefined ? null : cardEnterAt($enterIndex, 'backwards'))}
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  isolation: isolate;

  /* Card + leaf share ONE cell → identical box; the 1fr row makes both stretch to the outer grid
     row's height, so a stacked card stays exactly as tall as its non-stacked row-mates (equal-
     height rows preserved) and the leaf tracks the card, never a taller neighbour. */
  > * {
    grid-area: 1 / 1;
  }
`;

/* The single card "beneath" the front one: a SOLID, slightly lighter grey (never transparent, no
   border) offset down-right so only a thin corner peeks out. The front card keeps the standard
   solid card colour — same as every non-stacked card — so a stack never looks like a different
   colour, just the same card with one lighter layer under it. */
/* The leaves are the FRONT card colour, each stepped progressively DARKER (a receding stack): the first
   at 0.8 brightness, the second at 0.5, so the sequence reads card → darker → darkest instead of a
   uniform lighter rim. On DARK the card is near-black, so darkening it recedes it into the page.
   On LIGHT the card is white, so the SAME brightness filter would drag the leaves down to a heavy
   mid-grey that clashes with the surface — the leaves must go LIGHTER there, not darker. So light
   drops the filter and sits each leaf on an existing light-surface token (front one step above the
   deeper back one), harmonising with the white card. Dark is left byte-for-byte unchanged. */
export const StackLeaf = styled.div`
  z-index: -1;
  border-radius: 16px;
  background: ${COLORS.surfaceCard};
  filter: brightness(0.8);
  transform: translate(6px, 6px);

  :root[data-ows-theme='light'] & {
    background: ${COLORS.surfaceFaint};
    filter: none;
  }
`;

export const StackLeafBack = styled.div`
  z-index: -2;
  border-radius: 16px;
  background: ${COLORS.surfaceCard};
  filter: brightness(0.5);
  transform: translate(12px, 12px);

  :root[data-ows-theme='light'] & {
    background: ${COLORS.surfaceInset};
    filter: none;
  }
`;

/* Small "N versions" pill, top-left over the artwork. */
export const StackBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
`;

export const StackCount = styled.span`
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

/* "New" cue, top-right over the artwork: a finished regeneration the owner hasn't opened yet. The
   card face still shows the published/initial art, so this is the only hint a newer version exists.
   Cleared (via mark-seen) the moment the owner opens the editor. */
export const NewBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${COLORS.accent};
  color: ${COLORS.textBright};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

/* The banner's New look (its art drawn again in the brand's art style, not live yet), bottom-left over the artwork:
   a quiet pill like the version stack, red text only when it could not be drawn. */
export const NewLookChip = styled.span<{ $failed?: boolean }>`
  position: absolute;
  left: ${SPACE[8]};
  bottom: ${SPACE[8]};
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  color: ${({ $failed }) => ($failed ? COLORS.danger : COLORS.text)};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
`;

export const Card = styled.div<{ $muted?: boolean; $clickable?: boolean; $enterIndex?: number }>`
  ${hoverWash}
  /* Its grid's stagger as the list arrives; fill backwards, so a muted card keeps its dimmed opacity once in. */
  ${({ $enterIndex }) => ($enterIndex === undefined ? null : cardEnterAt($enterIndex, 'backwards'))}
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  /* Solid, opaque card surface (same for every card, stacked or not) so a stack's lighter leaf
     never bleeds through the front card's info strip. */
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const Art = styled.div<{ $frame?: 'light' | 'dark'; $plain?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 1;
  /* Opaque banners fill edge-to-edge on the themed card; only transparent logos get the auto-toned
     plate + padding so a white/dark mark stays legible. */
  padding: ${({ $plain }) => ($plain ? '0' : '16px')};
  background: ${({ $frame, $plain }) =>
    $plain
      ? COLORS.surfaceInset
      : $frame === 'light'
        ? CASINO_SETTINGS.previewLight
        : $frame === 'dark'
          ? CASINO_SETTINGS.previewDark
          : COLORS.surfaceInset};
  display: grid;
  place-items: center;
  transition: background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

/** The creative's art; fades in once loaded (it is given `revealOnLoad`). */
export const ArtImg = styled.img<{ $cover?: boolean }>`
  display: block;
  ${fadeInWhenReady}
  ${({ $cover }) =>
    $cover
      ? 'width: 100%; height: 100%; object-fit: cover;'
      : 'max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain;'}
`;

export const ArtEmpty = styled.div`
  color: ${COLORS.textFaint};
  display: grid;
  place-items: center;
`;

export const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
`;

/* Banner card body: title (on-banner headline) · subtitle · where it runs + links. */
export const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 11px 12px 12px;
`;

export const InfoHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const PlacementLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Desc = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${COLORS.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ContextRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

export const ContextLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const ContextChip = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.textMuted};
`;

export const ContextText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

/* The banner's link target — clickable, opens the brand's live site at that path. */
/* A subtle inline "preview / view on site" affordance. Works as an <a> (new-tab fallback) or, via
   as="button", as a reset button (no browser chrome) so the in-app preview trigger reads as the same
   quiet link, never a grey filled bar. */
export const LinkHint = styled.a`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  width: fit-content;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  appearance: none;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textFaint};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${COLORS.text};
  }
`;

/* Below-the-image actions (Download · Edit), so buttons never cover the artwork. */
export const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

export const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 9px;
  cursor: pointer;
  background: ${COLORS.surfaceMuted};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const LinkText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 500;
  color: ${COLORS.textFaint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
`;

export const Origin = styled.span<{ $origin: 'lumio' | 'custom' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 999px;
  color: ${({ $origin }) => ($origin === 'custom' ? COLORS.accent : COLORS.textMuted)};
  border: 1px solid ${({ $origin }) => ($origin === 'custom' ? COLORS.accent : COLORS.hairline)};
`;

export const OriginText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;
